import { LocalDate } from "@js-joda/core";
import { type FC, useCallback, useState } from "react";
import type { MetaAdsDatePreset } from "shared";
import { Select } from "../../components/Select";

interface DateRangeSelectorProps {
	from: string;
	to: string;
	onChange: (from: string, to: string) => void;
}

const presets: { label: string; value: MetaAdsDatePreset }[] = [
	{ label: "Today", value: "today" },
	{ label: "Yesterday", value: "yesterday" },
	{ label: "Last 7 Days", value: "last_7_days" },
	{ label: "Last 30 Days", value: "last_30_days" },
	{ label: "This Month", value: "this_month" },
	{ label: "Last Month", value: "last_month" },
	{ label: "Custom", value: "custom" },
];

function getPresetDates(preset: MetaAdsDatePreset): {
	from: string;
	to: string;
} {
	const today = LocalDate.now();
	switch (preset) {
		case "today":
			return { from: today.toString(), to: today.toString() };
		case "yesterday": {
			const yesterday = today.minusDays(1);
			return { from: yesterday.toString(), to: yesterday.toString() };
		}
		case "last_7_days":
			return {
				from: today.minusDays(6).toString(),
				to: today.toString(),
			};
		case "last_30_days":
			return {
				from: today.minusDays(29).toString(),
				to: today.toString(),
			};
		case "this_month":
			return {
				from: today.withDayOfMonth(1).toString(),
				to: today.toString(),
			};
		case "last_month": {
			const firstOfLastMonth = today.minusMonths(1).withDayOfMonth(1);
			const lastOfLastMonth = today.withDayOfMonth(1).minusDays(1);
			return {
				from: firstOfLastMonth.toString(),
				to: lastOfLastMonth.toString(),
			};
		}
		default:
			return {
				from: today.minusDays(29).toString(),
				to: today.toString(),
			};
	}
}

export function getDefaultDateRange(): { from: string; to: string } {
	try {
		const raw = sessionStorage.getItem("meta_ads_reporting_date_range");
		if (raw) {
			const parsed = JSON.parse(raw) as { from?: string; to?: string };
			if (parsed.from && parsed.to) {
				return { from: parsed.from, to: parsed.to };
			}
		}
	} catch {
		// Ignore invalid session data.
	}
	return getPresetDates("last_30_days");
}

export function persistDateRange(from: string, to: string): void {
	sessionStorage.setItem(
		"meta_ads_reporting_date_range",
		JSON.stringify({ from, to }),
	);
}

export const DateRangeSelector: FC<DateRangeSelectorProps> = ({
	from,
	to,
	onChange,
}) => {
	const [selectedPreset, setSelectedPreset] =
		useState<MetaAdsDatePreset>("last_30_days");

	const handlePresetChange = useCallback(
		(preset: MetaAdsDatePreset) => {
			setSelectedPreset(preset);
			if (preset !== "custom") {
				const dates = getPresetDates(preset);
				onChange(dates.from, dates.to);
			}
		},
		[onChange],
	);

	const isCustom = selectedPreset === "custom";

	return (
		<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
			<Select
				className="form-select form-select-sm"
				style={{ width: "auto" }}
				value={selectedPreset}
				onChange={(e) =>
					handlePresetChange(e.target.value as MetaAdsDatePreset)
				}
			>
				{presets.map((p) => (
					<option key={p.value} value={p.value}>
						{p.label}
					</option>
				))}
			</Select>
			{isCustom && (
				<>
					<input
						type="date"
						className="form-control form-control-sm"
						style={{ width: "auto" }}
						value={from}
						onChange={(e) => onChange(e.target.value, to)}
					/>
					<span>to</span>
					<input
						type="date"
						className="form-control form-control-sm"
						style={{ width: "auto" }}
						value={to}
						onChange={(e) => onChange(from, e.target.value)}
					/>
				</>
			)}
		</div>
	);
};
